import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const getInitials = (name = "") => {
  const p = name.trim().split(" ");
  return p.length === 1 ? p[0].slice(0, 2).toUpperCase() : (p[0][0] + p[p.length - 1][0]).toUpperCase();
};

const Step4Review = ({ position, associate, fte, selectedSkills, skills, startDate, endDate, notes }) => {
  const theme = useTheme();
  const t     = theme.custom;

  const skillNames = skills
    .filter((s) => selectedSkills.includes(s.skillId ?? s))
    .map((s) => s.skillName ?? s);

  const fmt = (iso) => iso
    ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

  return (
    <Box>
      <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.14em", textTransform: "uppercase", color: t.text3, mb: "12px" }}>
        Review & Submit
      </Typography>

      {/* Position card */}
      <Box sx={{ background: t.surface2, border: `1px solid ${t.border2}`, borderRadius: "3px", padding: "12px 14px", mb: "8px" }}>
        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.12em", textTransform: "uppercase", color: t.text3, mb: "6px" }}>
          Position
        </Typography>
        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.accent, mb: "2px" }}>
          {position?.positionCode}
        </Typography>
        <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 600, color: t.text }}>
          {position?.roleTitle}
        </Typography>
        <Typography sx={{ fontSize: "11px", color: t.text3, mt: "1px" }}>
  {position?.sowName} · {position?.positionType === "BIL" ? "Billed" : "Unbilled"}
</Typography>
<Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.text3, mt: "2px" }}>
  {position?.accountName}
</Typography>
        {position?.billedFte != null && (
          <Box sx={{
            mt: "8px",
            background: position?.positionType === "BIL" ? t.accent2 : t.gold2,
            border: `1px solid ${position?.positionType === "BIL" ? t.accent + "44" : t.gold + "44"}`,
            borderRadius: "2px", padding: "2px 8px",
            display: "inline-flex", alignItems: "center", gap: "5px",
          }}>
            <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: t.text3, letterSpacing: "0.08em" }}>
              BILLED FTE
            </Typography>
            <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", fontWeight: 700, color: position?.positionType === "BIL" ? t.accent : t.gold }}>
              {position?.billedFte}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Associate + FTE card */}
      <Box sx={{ background: t.surface2, border: `1px solid ${t.border2}`, borderRadius: "3px", padding: "12px 14px", mb: "8px", display: "flex", alignItems: "center", gap: "10px" }}>
        <Box sx={{
          width: "32px", height: "32px", borderRadius: "50%", flexShrink: 0,
          background: "rgba(40,179,180,0.15)", border: "1px solid rgba(40,179,180,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "'DM Mono', monospace", fontSize: "10px", fontWeight: 700, color: t.accent,
        }}>
          {getInitials(associate?.fullName)}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 600, color: t.text }}>
            {associate?.fullName}
          </Typography>
          <Typography sx={{ fontSize: "11px", color: t.text3 }}>
            Level {associate?.levelCode}
            {associate?.levelTitle ? ` · ${associate.levelTitle}` : ""}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "18px", fontWeight: 700, color: t.accent, lineHeight: 1 }}>
            {fte} FTE
          </Typography>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3, mt: "2px" }}>
            Requested allocation
          </Typography>
        </Box>
      </Box>

      {/* Skills + dates */}
      {(startDate || endDate) && (
        <Box sx={{ background: t.surface2, border: `1px solid ${t.border2}`, borderRadius: "3px", padding: "10px 14px", mb: "8px" }}>
          <Box sx={{ display: "flex", gap: "24px" }}>
            {startDate && (
              <Box>
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.1em", textTransform: "uppercase", color: t.text3, mb: "2px" }}>Start</Typography>
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: t.text2 }}>{fmt(startDate)}</Typography>
              </Box>
            )}
            {endDate && (
              <Box>
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.1em", textTransform: "uppercase", color: t.text3, mb: "2px" }}>End</Typography>
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: t.text2 }}>{fmt(endDate)}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Skills deployed */}
      <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.12em", textTransform: "uppercase", color: t.text3, mb: "6px" }}>
        Skills Deployed on This Engagement
      </Typography>
      {skillNames.length > 0 ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px", mb: "12px" }}>
          {skillNames.map((sk) => (
            <Box key={sk} sx={{ background: t.accent2, border: `1px solid ${t.accent}44`, borderRadius: "2px", padding: "2px 8px" }}>
              <Typography sx={{ fontSize: "11px", color: t.accent }}>{sk}</Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography sx={{ fontSize: "11px", color: t.text3, fontStyle: "italic", mb: "12px" }}>
          No skills selected
        </Typography>
      )}

      {/* Approval info box */}
      <Box sx={{ background: t.surface2, border: `1px solid ${t.border2}`, borderRadius: "3px", padding: "12px 14px" }}>
        <Typography sx={{ fontSize: "12px", color: t.text2, lineHeight: 1.7 }}>
          Request will be sent to{" "}
          <strong style={{ color: t.text }}>(PMO)</strong>{" "}
          for Gate 1 review. On approval it proceeds to{" "}
          <strong style={{ color: t.text }}>(SCG)</strong>{" "}
          for Gate 2. You can track progress in Status Flow.
        </Typography>
      </Box>
    </Box>
  );
};

export default Step4Review;